/**
 * ThreeEndpoint — Stores Three.js object references without propagation.
 *
 * The scene descriptor system (buildSceneDescriptor / buildPerNodeDescriptors)
 * handles mesh/geometry/light routing by walking the GraphIndex. Endpoint-to-endpoint
 * propagation of Three.js objects is not needed.
 *
 * One-time value push on connectTo ensures CameraNode receives the scene
 * from SceneNode when the connection is established.
 */

import type { IEndpoint } from '@/runtime/IEndpoint';
import type { INode } from '@/nodes/INode';
import { type DataTypeSpec, type DataFlow } from '@/nodes/Endpoint';
import type { StoreKey } from '@runtime/RuntimeStore';
import type { EndpointWithContext } from '@plugins/Plugin';
import type { Payload } from '@/graph/Payload';

/** Extract raw value from a Payload or raw value */
function unwrap(payload: any): any {
  return payload && typeof payload === 'object' && 'value' in payload
    ? payload.value : payload;
}

/** Ensure value is wrapped as a Payload, preserving existing metadata */
function ensurePayload(value: any): Payload {
  if (value && typeof value === 'object' && 'value' in value) return value;
  return { value };
}

export class ThreeEndpoint implements IEndpoint {
  readonly id: string;
  readonly dataType: DataTypeSpec;
  readonly dataFlow: DataFlow;
  readonly instancePath: string;
  readonly isInput!: boolean;
  readonly isOutput!: boolean;

  // Optional config fields (set via Object.assign)
  readonly label?: string;
  readonly primary?: boolean;
  readonly group?: string;
  readonly groupOrder?: number;
  readonly disabled?: boolean;
  readonly transient?: boolean;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly options?: readonly (string | number)[];

  node: INode;

  get storeKey(): StoreKey {
    return { instancePath: this.instancePath, nodeId: this.node.id, endpointId: this.id };
  }

  private object: any = null;
  private lastPayload: Payload = { value: null };
  // Connection tracking (no ongoing propagation)
  private targetEndpoints: IEndpoint[] = [];
  internalEndpoint: IEndpoint | null = null;

  constructor(ctx: EndpointWithContext) {
    this.instancePath = ctx.instancePath;
    this.node = ctx.node;
    // Apply all config fields (isInput, isOutput, label, group, etc.)
    Object.assign(this, ctx.endpoint);
    this.id = ctx.endpoint.id!;
    this.dataType = ctx.endpoint.dataType!;
    this.dataFlow = ctx.endpoint.dataFlow!;
  }

  getValue(): Payload {
    return this.lastPayload;
  }

  /**
   * Set value locally and propagate to connected targets.
   * Descriptor system handles mesh/geometry/light/scene routing via GraphIndex,
   * but shape values flow through endpoint propagation (Spline → Outline → Extrude).
   */
  setValue(payload: Payload, source?: IEndpoint): void {
    const wrapped = ensurePayload(payload);
    const raw = unwrap(payload);

    this.object = raw;
    this.lastPayload = wrapped;

    if (source && source !== this) {
      this.notifyNode(wrapped, source);
    }

    // Propagate to connected targets (needed for three:shape flow)
    if (this.internalEndpoint && this.internalEndpoint !== source) {
      this.internalEndpoint.receive(wrapped, this);
    }
    for (const target of this.targetEndpoints) {
      if (target !== source) {
        target.receive(wrapped, this);
      }
    }
  }

  /**
   * Receive value from upstream. Stores locally and notifies node.
   */
  receive(payload: Payload, source: IEndpoint): void {
    const wrapped = ensurePayload(payload);
    const raw = unwrap(payload);

    this.object = raw;
    this.lastPayload = wrapped;

    this.notifyNode(wrapped, source);
  }

  /**
   * Connect to a target endpoint.
   * Pushes current value for one-time delivery (e.g., scene → camera).
   */
  connectTo(target: IEndpoint): void {
    if (!this.targetEndpoints.includes(target)) {
      this.targetEndpoints.push(target);
      if (this.object !== null) {
        target.receive(this.lastPayload, this);
      }
    }
  }

  onConnectedFrom(_source: IEndpoint): void {}

  /** Set internal endpoint for subgraph boundary wiring. */
  connectInternal(internal: IEndpoint): void {
    this.internalEndpoint = internal;
  }

  disconnectFrom(target: IEndpoint): void {
    const idx = this.targetEndpoints.indexOf(target);
    if (idx !== -1) {
      this.targetEndpoints.splice(idx, 1);
    }
  }

  setConnectionDisabled(_target: IEndpoint, _disabled: boolean): void {
    // No-op: descriptor system handles mesh routing
  }

  dispose(): void {
    // Send null to targets so downstream nodes know the value is gone
    for (const target of this.targetEndpoints) {
      target.receive(null as any, this);
    }
    this.targetEndpoints = [];
    this.internalEndpoint = null;
    this.object = null;
    this.lastPayload = { value: null };
  }

  /** Dispatch to appropriate node handler based on dataFlow */
  private notifyNode(payload: any, source?: IEndpoint): void {
    switch (this.dataFlow) {
      case 'param':
        this.node.onParamChanged(payload, this.id, source);
        break;
      case 'internal':
        this.node.onInternalChanged(payload, this.id, source);
        break;
      case 'event':
        this.node.handleEvent(payload, this.id, source);
        break;
      case 'trigger':
        this.node.handleTrigger(payload, this.id, source);
        break;
    }
  }
}
